import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { ZIndex } from "~/common/constants";
import { Maximize2, X } from "~/common/redesign-v1/icons";

export function ChatImagePreviewTrigger({
  src,
  alt = "",
  children,
  className,
  triggerClassName,
}: {
  src: string;
  alt?: string;
  children: React.ReactNode;
  className?: string;
  triggerClassName?: string;
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const closeLabel = intl.formatMessage({ id: "chat.image.preview.close" });
  const downloadLabel = intl.formatMessage({ id: "chat.image.preview.download" });
  const downloadName = alt?.trim() || "image";

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className={clsx("tw-block tw-cursor-zoom-in tw-border-0 tw-bg-transparent tw-p-0 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand-green/40", triggerClassName, className)}
          aria-label={alt || closeLabel}
        >
          {children}
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="tw-fixed tw-inset-0 tw-bg-black/90" style={{ zIndex: ZIndex.CENTERED_DIALOG }} />
        <Dialog.Content
          className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4 tw-outline-none"
          style={{ zIndex: ZIndex.CENTERED_DIALOG + 1 }}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Dialog.Title className="tw-sr-only">{alt || closeLabel}</Dialog.Title>
          <Dialog.Close asChild>
            <button
              type="button"
              className="tw-absolute tw-right-4 tw-top-4 tw-z-10 tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-white/15 tw-p-0 tw-text-white tw-outline-none tw-transition-colors hover:tw-bg-white/25 focus-visible:tw-ring-2 focus-visible:tw-ring-white/50"
              aria-label={closeLabel}
            >
              <X className="tw-size-6" aria-hidden />
            </button>
          </Dialog.Close>
          <img src={src} alt={alt} className="tw-max-h-[75vh] tw-max-w-[75vw] tw-object-contain" crossOrigin="use-credentials" />
          <a
            href={src}
            download={downloadName}
            target="_blank"
            rel="noreferrer"
            className="tw-absolute tw-bottom-4 tw-right-4 tw-z-10 tw-inline-flex tw-h-10 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-white/15 tw-px-4 tw-text-sm tw-font-medium tw-text-white tw-no-underline tw-outline-none tw-transition-colors hover:tw-bg-white/25 focus-visible:tw-ring-2 focus-visible:tw-ring-white/50"
            onClick={(e) => e.stopPropagation()}
          >
            {downloadLabel}
          </a>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Opens a chat video in a contained dialog while leaving native controls on the inline preview. */
export function ChatVideoExpandButton({ src, alt = "", className }: { src: string; alt?: string; className?: string }) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const closeLabel = intl.formatMessage({ id: "chat.image.preview.close" });
  const downloadLabel = intl.formatMessage({ id: "chat.image.preview.download" });
  const expandLabel = intl.formatMessage({ id: "chat.video.expand", defaultMessage: "Open in window" });
  const downloadName = alt?.trim() || "video";

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className={clsx(
            "tw-absolute tw-bottom-1.5 tw-right-1.5 tw-z-10 tw-flex tw-size-7 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-black/50 tw-p-0 tw-text-white tw-outline-none tw-transition-colors hover:tw-bg-black/70 focus-visible:tw-ring-2 focus-visible:tw-ring-white/60",
            className,
          )}
          aria-label={expandLabel}
          title={expandLabel}
          onClick={(e) => e.stopPropagation()}
        >
          <Maximize2 className="tw-size-3.5" aria-hidden />
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="tw-fixed tw-inset-0 tw-bg-black/90" style={{ zIndex: ZIndex.CENTERED_DIALOG }} />
        <Dialog.Content
          className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4 tw-outline-none"
          style={{ zIndex: ZIndex.CENTERED_DIALOG + 1 }}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Dialog.Title className="tw-sr-only">{alt || expandLabel}</Dialog.Title>
          <Dialog.Close asChild>
            <button
              type="button"
              className="tw-absolute tw-right-4 tw-top-4 tw-z-10 tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-white/15 tw-p-0 tw-text-white tw-outline-none tw-transition-colors hover:tw-bg-white/25 focus-visible:tw-ring-2 focus-visible:tw-ring-white/50"
              aria-label={closeLabel}
            >
              <X className="tw-size-6" aria-hidden />
            </button>
          </Dialog.Close>
          {/* A fixed target box scales small videos while `object-contain` preserves their aspect ratio. */}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption -- chat file attachment */}
          {open ? <video className="tw-h-[70vh] tw-w-[70vw] tw-object-contain" controls autoPlay playsInline src={src} crossOrigin="use-credentials" /> : null}
          <a
            href={src}
            download={downloadName}
            target="_blank"
            rel="noreferrer"
            className="tw-absolute tw-bottom-4 tw-right-4 tw-z-10 tw-inline-flex tw-h-10 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-white/15 tw-px-4 tw-text-sm tw-font-medium tw-text-white tw-no-underline tw-outline-none tw-transition-colors hover:tw-bg-white/25 focus-visible:tw-ring-2 focus-visible:tw-ring-white/50"
            onClick={(e) => e.stopPropagation()}
          >
            {downloadLabel}
          </a>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
